"use client";

import { motion, useReducedMotion } from "framer-motion";
import {
  CalendarCheck,
  Check,
  CheckCheck,
  type LucideIcon,
  MousePointerClick,
  Reply,
  Send,
  Sparkles,
  Users,
} from "lucide-react";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { Avatar } from "@/ui/elements";
import { CountUp } from "./count-up";

const EASE = [0.16, 1, 0.3, 1] as const;

/* ================================================================== */
/* Compose card — the supporting "what you're sending" visual         */
/* ================================================================== */

function ConditionChip({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full border border-border bg-background px-2 py-0.5 text-[11px] font-medium text-foreground">
      {children}
    </span>
  );
}

export function BlastComposeCard({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "flex flex-col overflow-hidden rounded-2xl border border-border bg-background shadow-2xl shadow-black/10 dark:shadow-black/50",
        className,
      )}
    >
      {/* Audience header */}
      <div className="border-b border-border px-4 py-3">
        <div className="flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-foreground">
            <Users className="size-3.5 text-muted-foreground" />
            Lapsed members
          </span>
          <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
            1,240
          </span>
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          <ConditionChip>Last visit 30+ days</ConditionChip>
          <ConditionChip>Membership cancelled</ConditionChip>
        </div>
      </div>

      {/* Message draft — iOS-style image attachment above the text bubble */}
      <div className="flex flex-col items-end gap-2 px-4 py-4">
        <Image
          src="/images/website/new-year-message.jpg"
          alt="Member doing crunches in the studio"
          width={4680}
          height={3120}
          sizes="420px"
          className="h-auto w-full rounded-2xl object-cover"
        />
        <div className="max-w-[17rem] whitespace-pre-line rounded-2xl rounded-br-md bg-outgoing-bubble px-3 py-2 text-left text-[13px] leading-snug text-white">
          New year, fresh start 💪 Your first class back is on us all January. Tap to grab a spot and
          let's pick up right where you left off.
        </div>
        <p className="text-[10px] text-muted-foreground">Includes opt-out language</p>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between border-t border-border px-4 py-3">
        <span className="text-[12px] font-medium text-muted-foreground">Schedule</span>
        <span className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3.5 py-1.5 text-[13px] font-semibold text-primary-foreground">
          <Send className="size-3.5" />
          Send to 1,240
        </span>
      </div>
    </div>
  );
}

/* ================================================================== */
/* Results card — the live-impact differentiator (animated)           */
/* ================================================================== */

const JORDAN = { name: "Jordan Hayes" };
const TASHA = { name: "Tasha Bell" };

function Stat({
  icon: Icon,
  label,
  to,
  delay,
  format,
  suffix,
}: {
  icon: LucideIcon;
  label: string;
  to: number;
  delay: number;
  format?: (v: number) => string;
  suffix?: string;
}) {
  return (
    <div className="flex flex-col gap-1 p-3">
      <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
        <Icon className="size-3 shrink-0" />
        {label}
      </span>
      <span className="text-xl font-bold tabular-nums tracking-tight text-foreground">
        <CountUp to={to} delay={delay} format={format} />
        {suffix ? <span className="text-sm font-medium text-muted-foreground">{suffix}</span> : null}
      </span>
    </div>
  );
}

function ReplyChip({
  person,
  children,
  reduce,
  delay,
}: {
  person: { name: string; src?: string };
  children: React.ReactNode;
  reduce: boolean;
  delay: number;
}) {
  return (
    <motion.div
      initial={reduce ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: EASE, delay }}
      className="flex items-center gap-2"
    >
      <Avatar name={person.name} src={person.src} size="xs" />
      <div className="min-w-0 flex-1 rounded-xl rounded-bl-md border border-border bg-incoming-bubble px-2.5 py-1.5">
        <span className="text-[12px] leading-snug text-foreground">{children}</span>
      </div>
    </motion.div>
  );
}

export function BlastResultsCard({ className }: { className?: string }) {
  const reduce = useReducedMotion() ?? false;

  return (
    <div
      className={cn(
        "overflow-hidden rounded-2xl border border-border bg-background",
        className,
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-lg border border-border bg-muted/50 text-muted-foreground">
            <Sparkles className="size-4" />
          </span>
          <div className="min-w-0">
            <p className="truncate text-[13px] font-semibold text-foreground">Win-back promo</p>
            <p className="truncate text-[11px] text-muted-foreground">Text · 1,240 recipients</p>
          </div>
        </div>
        <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-success/10 px-2 py-0.5 text-[11px] font-medium text-success">
          <Check className="size-3" />
          Sent
        </span>
      </div>

      {/* Metric grid — delivered leads, then the funnel */}
      <div className="grid grid-cols-2 gap-px border-t border-border bg-border [&>*]:bg-background">
        <Stat icon={CheckCheck} label="Delivered" to={1212} delay={0.1} suffix=" / 1,240" />
        <Stat icon={MousePointerClick} label="Clicks" to={318} delay={0.25} />
        <Stat icon={Reply} label="Replies" to={87} delay={0.4} />
        <Stat icon={CalendarCheck} label="Booked" to={41} delay={0.55} />
      </div>

      {/* Replies flowing back */}
      <div className="space-y-2 border-t border-border px-4 py-3">
        <ReplyChip person={JORDAN} reduce={reduce} delay={0.9}>
          Count me in! 🙌
        </ReplyChip>
        <ReplyChip person={TASHA} reduce={reduce} delay={1.05}>
          Just booked Saturday, thank you!
        </ReplyChip>
      </div>
    </div>
  );
}
